// Areas (DESIGN §3, DATA_CONTRACT I): one card per open program (AP-n) — the engine's readiness document (its own procedure
// counts), the program's workpapers with their state, the processes the engine lists as not yet run (each with its run
// action), exceptions and open procedures, and the area's completion. A process that ran leaves the engine's queue; the
// engine does not publish a per-process "ran" list yet (engine support needed), so run processes are not listed here.
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { Card, EmptyState, ErrorState, LoadState, SignoffCircles, StatusPill } from "../../components";
import type { Action } from "../../data/types";
import { countsText } from "../../components";
import { useLoad } from "../../lib/hooks";
import { useEngagement } from "./Layout";
import { Act, NotPublished, QueueList, SmartDoc, asStrs, useAct, useDocs } from "./shared";

/** The program a queued process belongs to, as the engine wrote it at the start of `what` ("NPO-AP-5 (cash): …"). */
export const processProgram = (a: Action) => a.what.match(/^([A-Z]+-[A-Z]+-\d+[A-Z-]*)\s*(?:\(([^)]+)\))?:/);

export function AreasPage() {
  const { id, model } = useEngagement();
  const { ds, catalogue } = useApp();
  const { open, drawer } = useAct();
  const completable = new Set((catalogue.get("area.complete")?.inputSchema.properties?.area?.enum ?? []).map(String));
  const areas = useDocs("areas");
  const procs = useLoad(() => ds.table(id, "procedures"), [ds, id]);
  const processes = model.actions.filter((a) => a.kind === "process");
  const other = processes.filter((a) => !processProgram(a) || !(areas.data ?? []).some((d) => d.kind === processProgram(a)![1]));
  return (
    <div className="space-y-4">
      <LoadState state={areas}>
        {(as) => as.length === 0 ? <EmptyState title="No open programs published yet.">Programs are tailored in S13; each open one appears here.</EmptyState> : (
          <div className="grid gap-4 xl:grid-cols-2">
            {as.map((a) => {
              const doc = (a.doc ?? {}) as Record<string, unknown>;
              const wpIds = asStrs(doc.workpapers).length ? asStrs(doc.workpapers) : [a.kind];
              const wps = model.workpapers.filter((w) => wpIds.includes(w.id));
              const mine = processes.filter((p) => processProgram(p)?.[1] === a.kind);
              const areaName = mine.map((p) => processProgram(p)?.[2]).find(Boolean);
              const exceptions = (procs.data ?? []).filter((p) => wpIds.includes(p.workpaper) && p.status === "exception");
              const counts = doc.procedures && typeof doc.procedures === "object" ? (doc.procedures as Record<string, number>) : null;
              return (
                <Card key={a.kind} className="min-w-0" title={<span><span className="font-mono">{a.kind}</span>{areaName ? <span className="font-normal text-muted"> · {areaName}</span> : null}</span>}
                  action={areaName && completable.has(areaName) ? <Act command="area.complete" title={`Complete the ${areaName} area`} open={open} fixed={{ area: areaName }} label="Complete area…" /> : undefined}>
                  {a.error ? <ErrorState error={`The engine could not compute this area: ${a.error}`} /> : (
                    <div className="space-y-3">
                      {counts && <p className="text-sm"><span className="text-muted">Procedures (engine count):</span> {countsText(counts) || "none"}</p>}
                      <SmartDoc doc={doc} open={open} skip={["workpapers", "procedures"]} />
                      {wps.length > 0 && (
                        <ul className="space-y-1 text-sm">{wps.map((w) => (
                          <li key={w.id} className="flex flex-wrap items-center gap-2"><Link className="font-mono hover:underline" to={`/e/${id}/details/wp/${w.id}`}>{w.id}</Link>
                            <StatusPill state={w.state} label={w.stateLabel} /><SignoffCircles signoffs={w.signoffs} scheme={w.signoffScheme} />
                            {w.openProcedures > 0 && <StatusPill state="open" label={`${w.openProcedures} open procedures`} />}{w.openNotes > 0 && <StatusPill state="open" label={`${w.openNotes} open notes`} />}</li>))}</ul>
                      )}
                      {exceptions.length > 0 && (
                        <div><h3 className="text-xs font-medium uppercase tracking-wide text-blocked">Exceptions</h3>
                          <ul className="list-disc pl-4 text-sm">{exceptions.map((p) => <li key={p.id}><span className="font-mono text-xs">{p.task}</span> {p.result}</li>)}</ul></div>
                      )}
                      <div>
                        <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">Processes not yet run · {mine.length}</h3>
                        <QueueList actions={mine} open={open} empty="The engine lists no process waiting for this program." />
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </LoadState>
      {other.length > 0 && <Card title="Other processes in the engine's queue" bodyClass="p-3"><QueueList actions={other} open={open} /></Card>}
      <NotPublished what="the processes already run per program, samples, confirmations per area and JE testing results" command="area.schedule / confirm.list / je list" />
      {drawer}
    </div>
  );
}
